<template>
  <div class="szw-regio" style="font-family: 'Plus Jakarta Sans', Inter, sans-serif;">

    <!-- Hero -->
    <section class="szw-regio-hero">
      <div class="szw-regio-hero__bg">
        <svg class="szw-regio-hero__bg-shape" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#ffffff" opacity="0.05"/>
        </svg>
        <div class="szw-regio-hero__blob szw-regio-hero__blob--gold"></div>
        <div class="szw-regio-hero__blob szw-regio-hero__blob--teal"></div>
      </div>

      <div class="szw-regio-container szw-regio-hero__content">
        <div class="tw-flex items-start gap-3">
          <div class="szw-regio-hero__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 28px; color: #071c5b;">location_on</span>
          </div>
          <div>
            <div class="tw-flex items-center gap-2">
              <h1 class="szw-regio-hero__title">{{ $t('samergo_regio_title') }}</h1>
              <span v-if="totalCount" class="szw-regio-hero__count">
                {{ totalCount }} professionals
              </span>
            </div>
            <p class="szw-regio-hero__subtitle">{{ $t('samergo_regio_subtitle') }}</p>
            <div class="tw-flex items-center gap-1.5 mt-1">
              <span class="material-symbols-outlined" style="font-size: 14px; color: rgba(219,234,254,0.7);">location_on</span>
              <span style="font-size: 0.82rem; color: rgba(219,234,254,0.7);">{{ locationLabel || $t('samergo_regio_no_location_title') }}</span>
              <button class="szw-regio-hero__edit-btn" @click="openProfileModal" :title="$t('samergo_regio_edit_location')">
                <span class="material-symbols-outlined" style="font-size: 13px;">edit</span>
              </button>
            </div>
          </div>
          <button v-if="canAddActor" class="szw-regio-btn szw-regio-btn--add-actor" @click="createActor">
            <span class="material-symbols-outlined" style="font-size: 18px;">add</span>
            {{ $t('add_actor_add_button', { actor: actorLabel }) }}
          </button>
        </div>

        <!-- Search -->
        <div v-if="actors.length || searchQuery" class="szw-regio-hero__search-wrapper">
          <div class="szw-regio-hero__search-glow"></div>
          <div class="szw-regio-hero__search-box">
            <span class="material-symbols-outlined szw-regio-hero__search-icon">search</span>
            <input
              v-model="searchQuery"
              class="szw-regio-hero__search-input"
              :placeholder="$t('samergo_regio_search_placeholder')"
              type="text"
            />
            <button v-if="searchQuery" class="szw-regio-hero__search-clear" @click="searchQuery = ''">
              <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="szw-regio-section">
      <div class="szw-regio-container">

        <!-- No location set -->
        <div v-if="!hasLocation" class="szw-regio-empty">
          <div class="szw-regio-empty__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">location_off</span>
          </div>
          <h2 class="szw-regio-empty__title">{{ $t('samergo_regio_no_location_title') }}</h2>
          <p class="szw-regio-empty__body">{{ $t('samergo_regio_no_location_body') }}</p>
          <button class="szw-regio-btn szw-regio-btn--primary" @click="openProfileModal">
            <span class="material-symbols-outlined" style="font-size: 18px;">my_location</span>
            {{ $t('samergo_regio_set_region') }}
          </button>
        </div>

        <!-- Loading skeletons -->
        <div v-else-if="loading && !actors.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="n in 9" :key="n" class="bg-white rounded-2xl border border-[#c6c5d2] p-6">
            <div class="tw-flex items-center gap-3 mb-4">
              <div class="w-12 h-12 rounded-xl flex-shrink-0 szw-regio-shimmer"></div>
              <div class="tw-flex flex-col flex-1 gap-2">
                <div class="h-4 w-3/4 rounded szw-regio-shimmer"></div>
                <div class="h-3 w-1/2 rounded szw-regio-shimmer"></div>
              </div>
            </div>
            <div class="h-3 w-full rounded szw-regio-shimmer mb-2"></div>
            <div class="h-3 w-4/5 rounded szw-regio-shimmer mb-6"></div>
            <div class="h-8 w-32 rounded-lg szw-regio-shimmer"></div>
          </div>
        </div>

        <!-- No results -->
        <div v-else-if="hasLocation && !loading && !filteredActors.length" class="szw-regio-empty">
          <div class="szw-regio-empty__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">search_off</span>
          </div>
          <p class="szw-regio-empty__body">
            <template v-if="searchQuery">{{ $t('samergo_regio_no_results_search', { query: searchQuery }) }}</template>
            <template v-else>{{ $t('samergo_regio_no_results') }}</template>
          </p>
          <button v-if="searchQuery" class="szw-regio-btn szw-regio-btn--ghost" @click="searchQuery = ''">{{ $t('samergo_regio_clear_search') }}</button>
        </div>

        <!-- Actor grid -->
        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <SamergoActorCard
              v-for="actor in filteredActors"
              :key="actor.id"
              :actor="actor"
              :contributors="actor.contributors || []"
              :claimed-actors="samergoMyClaims"
              :description="localizedDisplayProperty(actor, 'description')"
              hide-contact
            />
          </div>

          <!-- Infinite scroll -->
          <InfiniteLoading
            class="tw-flex justify-center mt-10"
            :loading="loadingMore"
            :complete="!hasMore"
            @load="loadMore"
          />
        </template>

      </div>
    </section>

    <SimplifiedFooterSamergo/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import SamergoActorCard from '@/components/Samergo/SamergoActorCard.vue'
  import InfiniteLoading from '@/components/InfiniteLoading/InfiniteLoading.vue'
  import { actorCount, fetchActors, fetchContributors } from '@/api/actors'
  import { fetchMyClaims } from '@/api/network-contacts.js'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'
  import { ACTION_TYPES as NETWORK_ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import UserMixin from '@/util/UserMixin'
  import { ACTION_TYPES } from '@/store/modules/user'
  import TranslationsMixin from '@/util/TranslationsMixin'

  const PAGE_SIZE = 18

  const SAMERGO_REGIONS = [
    { key: 'albrandswaard', label: 'Albrandswaard', lat: 51.8557, lon: 4.4108, portfolio: 'lDnrgjNkQJ' },
    { key: 'barendrecht', label: 'Barendrecht', lat: 51.8553, lon: 4.5347, portfolio: 'JjVgNKmx2p' },
    { key: 'goeree-overflakkee', label: 'Goeree-Overflakkee', lat: 51.7453, lon: 4.0653, portfolio: '4EnmG5axQr' },
    { key: 'nissewaard', label: 'Nissewaard', lat: 51.8353, lon: 4.2697, portfolio: 'gXVp4APVQp' },
    { key: 'ridderkerk', label: 'Ridderkerk', lat: 51.8653, lon: 4.5967, portfolio: 'bGnGaejxNX' },
    { key: 'voorne-aan-zee', label: 'Voorne aan Zee', lat: 51.8753, lon: 4.0567, portfolio: 'GZx6lRjn8A' },
    { key: 'hoeksche-waard', label: 'Hoeksche-Waard', lat: 51.806599, lon: 4.348712, portfolio: 'RLn7EmEx0w' },
  ]

  export default defineComponent({
    name: 'ActorsSimplifiedRegio',
    components: { SimplifiedFooterSamergo, SamergoActorCard, InfiniteLoading },
    mixins: [TaxonomyMixin, UserMixin, TranslationsMixin],
    data() {
      return {
        actors: [] as any[],
        loading: false,
        loadingMore: false,
        offset: 0,
        hasMore: false,
        totalCount: 0,
        searchQuery: '',
        samergoMyClaims: [] as any[],
      }
    },
    async mounted() {
      fetchMyClaims().then((claims: any[]) => {
        this.samergoMyClaims = claims
      }).catch(() => {
      })
      this.$store.dispatch(NETWORK_ACTION_TYPES.FETCH)
      if (this.hasLocation) {
        await this.fetch()
      }

      this.$bus.on('actorCreated', () => {
        this.fetch()
      })
    },
    watch: {
      'profile.samergo_region'(val: string, old: string) {
        if (val !== old) {
          this.fetch()
          this.$store.dispatch(ACTION_TYPES.REFRESH_PROFILE)
        }
      },
    },
    computed: {
      profile() {
        return this.$store.state.user.profile || {}
      },
      activeRegion(): typeof SAMERGO_REGIONS[number] | undefined {
        return SAMERGO_REGIONS.find(r => r.key === this.profile.samergo_region)
      },
      hasLocation(): boolean {
        return !!this.activeRegion
      },
      locationLabel(): string {
        return this.activeRegion?.label ?? ''
      },
      regionPortfolio(): string {
        return this.activeRegion?.portfolio ?? ''
      },
      canAddActor(): boolean {
        if (!this.regionPortfolio) return false
        const contribPortfolios: string[] = this.$store.getters.userContributionPortfolios
        console.log(this.$store.getters.userContributionPortfolios, this.regionPortfolio, contribPortfolios.includes(this.regionPortfolio))
        return contribPortfolios.includes(this.regionPortfolio)
      },
      filteredActors(): any[] {
        if (!this.searchQuery.trim()) return this.actors
        const q = this.searchQuery.toLowerCase()
        return this.actors.filter((a: any) =>
          (a.name && a.name.toLowerCase().includes(q)) ||
          (a.short_description && a.short_description.toLowerCase().includes(q))
        )
      },
    },
    methods: {
      async fetch() {
        if (!this.regionPortfolio) return
        this.loading = true
        this.offset = 0
        this.actors = []
        this.hasMore = false
        this.totalCount = 0
        actorCount({ portfolio: this.regionPortfolio })
          .then((res: any) => {
            this.totalCount = res?.count ?? 0
          })
          .catch(() => {
          })
        try {
          const res: any = await fetchActors({ portfolio: this.regionPortfolio, limit: PAGE_SIZE, offset: 0, order: '-created_at' })
          const raw: any[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
          this.hasMore = raw.length === PAGE_SIZE
          this.offset = raw.length
          this.actors = await this.withContributors(raw)
        } catch {
          this.actors = []
        } finally {
          this.loading = false
        }
      },
      openProfileModal() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      createActor() {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { portfolioId: this.regionPortfolio })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      async loadMore() {
        if (this.loadingMore || !this.hasMore || !this.regionPortfolio) return
        this.loadingMore = true
        try {
          const res: any = await fetchActors({ portfolio: this.regionPortfolio, limit: PAGE_SIZE, offset: this.offset, order: '-created_at' })
          const raw: any[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
          this.hasMore = raw.length === PAGE_SIZE
          this.offset += raw.length
          const withC = await this.withContributors(raw)
          this.actors = [...this.actors, ...withC]
        } catch {
          // silently fail
        } finally {
          this.loadingMore = false
        }
      },
      async withContributors(actors: any[]): Promise<any[]> {
        const results = await Promise.allSettled(
          actors.map(a => fetchContributors(a.id, { includeOwners: true }))
        )
        return actors
          .map((a, i) => {
            const r = results[i]
            const contributors: any[] = r.status === 'fulfilled' && Array.isArray(r.value) ? r.value : []
            return { ...a, contributors }
          })
      },
    },
  })
</script>

<style scoped lang="scss">
  $szw-navy: #071c5b;
  $szw-surface: #f8f9fa;
  $szw-border: #c6c5d2;
  $szw-text: #191c1d;
  $szw-text-muted: #454650;

  .szw-regio {
    background: $szw-surface;
    min-height: 100vh;
    color: $szw-text;
  }

  .szw-regio-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  // ── Hero ──────────────────────────────────────────────────────────────────

  .szw-regio-hero {
    position: relative;
    background: $szw-navy;
    padding: 64px 0 80px;
    overflow: hidden;
  }

  .szw-regio-hero__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .szw-regio-hero__bg-shape {
    position: absolute;
    top: 0;
    right: 0;
    width: 60%;
    height: 100%;
  }

  .szw-regio-hero__blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);

    &--gold {
      top: -40px;
      right: 10%;
      width: 300px;
      height: 300px;
      background: #f5c343;
      opacity: 0.18;
    }

    &--teal {
      bottom: -60px;
      left: 5%;
      width: 240px;
      height: 240px;
      background: #006e29;
      opacity: 0.22;
    }
  }

  .szw-regio-hero__content {
    position: relative;
    z-index: 10;
  }

  .szw-regio-hero__icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: #ffdf9a;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .szw-regio-hero__title {
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 700;
    color: white;
    line-height: 1.2;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .szw-regio-hero__edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: rgba(219, 234, 254, 0.8);
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
      color: white;
    }
  }

  .szw-regio-hero__subtitle {
    font-size: 0.95rem;
    color: rgba(219, 234, 254, 0.7);
    margin-top: 4px;
  }

  .szw-regio-hero__count {
    display: inline-block;
    background: rgba(255, 255, 255, 0.12);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 6px 18px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .szw-regio-hero__search-wrapper {
    position: relative;
    margin-top: 32px;
    max-width: 640px;
  }

  .szw-regio-hero__search-glow {
    position: absolute;
    inset: -4px;
    background: linear-gradient(to right, #ffdf9a, #7df98f);
    border-radius: 16px;
    filter: blur(8px);
    opacity: 0.25;
  }

  .szw-regio-hero__search-box {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 14px;
    padding: 6px 6px 6px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .szw-regio-hero__search-icon {
    padding: 0 12px 0 16px;
    color: $szw-navy;
    flex-shrink: 0;
  }

  .szw-regio-hero__search-input {
    flex: 1;
    padding: 10px 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 0.95rem;
    font-family: inherit;
    color: $szw-text;

    &::placeholder {
      color: #757681;
    }
  }

  .szw-regio-hero__search-clear {
    background: #f3f4f5;
    border: none;
    color: $szw-text-muted;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: 8px;
    margin-right: 4px;
    transition: background 0.15s;

    &:hover {
      background: $szw-border;
    }
  }

  // ── Section ───────────────────────────────────────────────────────────────

  .szw-regio-section {
    padding: 64px 0 80px;
    min-height: calc(100vh - 400px);
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  .szw-regio-empty {
    text-align: center;
    padding: 80px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .szw-regio-empty__icon-wrap {
    width: 96px;
    height: 96px;
    background: #ffdf9a;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .szw-regio-empty__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
  }

  .szw-regio-empty__body {
    font-size: 0.95rem;
    color: $szw-text-muted;
    max-width: 420px;
    line-height: 1.6;
    margin: 0;
  }

  // ── Buttons ───────────────────────────────────────────────────────────────

  .szw-regio-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    margin-top: 8px;
    transition: opacity 0.15s, transform 0.1s;

    &:hover:not(:disabled) {
      opacity: 0.88;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--primary {
      background: $szw-navy;
      color: white;
    }

    &--ghost {
      background: white;
      color: $szw-text-muted;
      border: 1px solid $szw-border;
    }

    &--add-actor {
      background: #ffdf9a;
      color: $szw-navy;
      margin-top: 0;
      margin-left: auto;
    }
  }

  // ── Shimmer ───────────────────────────────────────────────────────────────

  @keyframes szw-shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .szw-regio-shimmer {
    background: linear-gradient(90deg, #e0e2e8 25%, #edeef2 50%, #e0e2e8 75%);
    background-size: 800px 100%;
    animation: szw-shimmer 1.4s infinite linear;
  }
</style>
